import React, { ChangeEvent, useEffect, useState } from 'react';

import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { JBColors, JBIcon } from '@bjb-gcrm/gcrm-frontend-components';
import { ColumnDefinition, HeadCellFilterType } from 'components/DataTable/DataTableTypes';
import theme from 'theme';
import { formatEnum } from 'utils/stringUtil';

const useStyles = makeStyles(() => ({
  filterContainer: {
    position: 'relative',
    display: 'inline-flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  iconButton: {
    padding: 0,
  },
  paper: {
    borderRadius: 0,
    border: `1px solid ${JBColors.support.greenSmoke[50]}`,
    minWidth: 144,
    padding: theme.spacing(1, 3),
  },
  label: {
    width: '100%',
  },
  divider: {
    margin: theme.spacing(0, 1),
  },
}));

type Props<T> = {
  data: any;
  headCell: ColumnDefinition<T>;
  handleSort: (selectedFilters: { [key in keyof T]: string }[]) => void;
  handleSearch: (searchString: React.SetStateAction<string>, searchKey: keyof T) => void;
  shouldFormatEnum?: boolean;
};

export const createSet = (array: any[], createSetByProperty: any) =>
  Array.from(new Set(array?.map((item: any) => item[createSetByProperty])));

export const DataTableFilter = <T,>({
  data,
  headCell,
  handleSort,
  handleSearch,
  shouldFormatEnum = false,
}: Props<T>) => {
  const classes = useStyles();
  const { label, id, filter, mapFilterValues } = headCell;
  const filterValues = headCell.filterValues ?? createSet(data, id);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<{ [key in keyof T]: string }[]>([]);
  const [inputValue, setInputValue] = React.useState('');

  useEffect(() => {
    const selectedFilters: any =
      !!filterValues &&
      filterValues.map((filterValue: any) => {
        return { [id]: filterValue };
      });
    setSelectedFilters(selectedFilters ?? []);
    handleSort(selectedFilters);
  }, []);

  const handleAllChange = (event: ChangeEvent<HTMLInputElement> | null, checked: boolean) => {
    if (checked) {
      const selectedFilters: any =
        !!filterValues &&
        filterValues.map((filterValue: any) => {
          return { [id]: filterValue };
        });
      setSelectedFilters(selectedFilters ?? []);
      handleSort(selectedFilters);
    } else {
      handleSort([]);
      setSelectedFilters([]);
    }
  };

  function handleInputChange(e: { target: { value: React.SetStateAction<string> } }) {
    setInputValue(e.target.value);
    handleSearch(e.target.value, id);
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const name = event.target.name;

    if (checked) {
      setSelectedFilters([...(selectedFilters as any), { [id]: name }]);
      handleSort([...(selectedFilters as any), { [id]: name }]);
    } else {
      setSelectedFilters(selectedFilters.filter((selectedFilter) => selectedFilter[id] !== name));
      handleSort(selectedFilters.filter((selectedFilter) => selectedFilter[id] !== name));
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box className={classes.filterContainer}>
        <>
          <Typography sx={{ color: JBColors.support.greenSmoke[80] }}>{label}</Typography>
          <IconButton
            aria-haspopup="true"
            id={`${label}-icon`}
            disableRipple
            disableFocusRipple
            className={classes.iconButton}
            onClick={handleClick}
          >
            <JBIcon
              iconName="filter"
              fill={
                filterValues.length === selectedFilters.length
                  ? JBColors.support.greenSmoke[60]
                  : JBColors.core.reflexBlue[100]
              }
            />
          </IconButton>
        </>
      </Box>
      <Menu
        classes={{ paper: classes.paper }}
        id="filter-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <FormGroup>
          {filter === HeadCellFilterType.SEARCHFILTER && (
            <FormControl fullWidth>
              <TextField
                placeholder={headCell.filterInputPlaceholder ?? 'Search a value...'}
                value={inputValue}
                onChange={(event) => handleInputChange(event)}
                InputProps={{ inputProps: { min: 0 } }}
                type={headCell.filterInputType ?? 'text'}
              />
            </FormControl>
          )}

          {filter === HeadCellFilterType.CHECKBOXFILTER && (
            <>
              <MenuItem id="filter-menu-item" data-testid="filter-menu-item">
                <FormControlLabel
                  key="filter-checkbox-all"
                  className={classes.label}
                  control={
                    <Checkbox
                      id="filter-checkbox-all"
                      checked={!!filterValues && selectedFilters.length === filterValues.length}
                      onChange={handleAllChange}
                      color="primary"
                      name="All"
                      inputProps={
                        {
                          'data-testid': 'filter-checkbox-all',
                        } as React.InputHTMLAttributes<HTMLInputElement>
                      }
                    />
                  }
                  label="All"
                />
              </MenuItem>
              <Divider className={classes.divider} />

              {filterValues &&
                filterValues.map((filterValue, i) => (
                  <MenuItem
                    key={`filter-checkbox-item-${i}`}
                    id={`filter-checkbox-item-${filterValue}`}
                    data-testid={`filter-item-${filterValue}`}
                  >
                    <FormControlLabel
                      key={`filter-item-label-${i}`}
                      className={classes.label}
                      control={
                        <Checkbox
                          id={`checkbox-${filterValue}`}
                          checked={selectedFilters.some((selectedFilter) => selectedFilter[id] === filterValue)}
                          onChange={handleChange}
                          color="primary"
                          name={filterValue}
                          inputProps={
                            {
                              'data-testid': `checkbox-${filterValue}`,
                            } as React.InputHTMLAttributes<HTMLInputElement>
                          }
                        />
                      }
                      label={
                        shouldFormatEnum
                          ? formatEnum(filterValue)
                          : mapFilterValues
                            ? mapFilterValues(filterValue)
                            : filterValue
                      }
                    />
                  </MenuItem>
                ))}
            </>
          )}
        </FormGroup>
      </Menu>
    </>
  );
};
